import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, HostListener, ElementRef, ViewChild } from '@angular/core';
import { Invoice } from 'src/app/shared/models/invoice.interface';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { GeneralService } from 'src/app/services/general/general.service';

declare var bootstrap: any;

@Component({
  selector: 'app-view-invoice',
  templateUrl: './view-invoice.component.html',
  styleUrls: ['./view-invoice.component.scss']
})
export class ViewInvoiceComponent extends DestructorComponent implements OnInit, OnDestroy, OnChanges {
  @Input() invoices: Invoice[] = [];
  @Input() selectedFormats: string[] = [];
  @ViewChild('modalFormatDropdownContainer') modalFormatDropdownContainer!: ElementRef;

  isGenerating: boolean = false;
  selectedViewMode: string = 'merged'; // Default to merged mode
  pdfUrl: string = '';
  processedInvoices: Invoice[] = []; // Cache processed invoices
  editableData: any = null; // Store editable data from dummy-invoice component
  activeFormatTab: string = ''; // Currently active format tab
  selectedFormatsInModal: string[] = []; // Formats selected in modal
  showFormatDropdown: boolean = false;
  activeInvoiceIndex: number = 0; // Track active invoice for export certificate tabs

  // Progress tracking for PDF generation
  currentGeneratingIndex: number = 0;
  totalToGenerate: number = 0;

  // Available PDF formats
  pdfFormats = [
    { value: 'plain_invoice', label: 'Imperial Solutions Format' },
    { value: 'caricom_invoice', label: 'Caricom Invoice Format' },
    { value: 'commercial_invoice', label: 'Commercial Invoice Format' },
    { value: 'standard_invoice', label: 'Dummy Invoice Format' },
    { value: 'shipping_information', label: 'Shipping Information Format' },
    { value: 'export_certificate', label: 'Export Certificate Format' },
    { value: 'house_bill_landing', label: 'House Bill of Lading Format' },
    { value: 'ctn_invoice', label: 'CTN Invoice' },
    { value: 'ctn_invoice_excel', label: 'CTN Invoice Excel' }
  ];

  // Available view modes
  viewModes = [
    { value: 'individual', label: 'Individual' },
    { value: 'merged', label: 'Merged' }
  ];

  constructor(private _generalService: GeneralService) {
    super();
  }

  ngOnInit(): void {
    this.updateProcessedInvoices();
    this.initializeFormats();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['invoices']) {
      this.updateProcessedInvoices();
    }
    if (changes['selectedFormats']) {
      this.initializeFormats();
    }
  }

  private initializeFormats(): void {
    // Initialize selected formats from input
    this.selectedFormatsInModal = [...this.selectedFormats];
    // Set active tab to first selected format
    if (this.selectedFormatsInModal.length > 0) {
      this.activeFormatTab = this.selectedFormatsInModal[0];
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  onFormatTabChange(formatValue: string): void {
    this.activeFormatTab = formatValue;
    // Reset active invoice index when switching format tabs
    this.activeInvoiceIndex = 0;
    console.log('Active format tab changed to:', this.activeFormatTab);
  }

  onInvoiceTabChange(index: number): void {
    this.activeInvoiceIndex = index;
    console.log('Active invoice index changed to:', this.activeInvoiceIndex);
  }

  onFormatToggle(formatValue: string): void {
    const index = this.selectedFormatsInModal.indexOf(formatValue);
    if (index > -1) {
      this.selectedFormatsInModal.splice(index, 1);
      // If we removed the active tab, switch to another tab
      if (this.activeFormatTab === formatValue && this.selectedFormatsInModal.length > 0) {
        this.activeFormatTab = this.selectedFormatsInModal[0];
      }
    } else {
      this.selectedFormatsInModal.push(formatValue);
      // If this is the first format, make it active
      if (this.selectedFormatsInModal.length === 1) {
        this.activeFormatTab = formatValue;
      }
    }
    console.log('Selected formats in modal:', this.selectedFormatsInModal);
  }

  isFormatSelected(formatValue: string): boolean {
    return this.selectedFormatsInModal.includes(formatValue);
  }

  toggleFormatDropdown(): void {
    this.showFormatDropdown = !this.showFormatDropdown;
  }

  closeFormatDropdown(): void {
    this.showFormatDropdown = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.showFormatDropdown && this.modalFormatDropdownContainer) {
      const clickedInside = this.modalFormatDropdownContainer.nativeElement.contains(event.target);
      if (!clickedInside) {
        this.closeFormatDropdown();
      }
    }
  }

  onViewModeChange(): void {
    console.log('View mode changed to:', this.selectedViewMode);
    this.updateProcessedInvoices();
  }

  // Update processed invoices when view mode or invoices change
  private updateProcessedInvoices(): void {
    if (this.selectedViewMode === 'merged' && this.invoices.length > 1) {
      this.processedInvoices = this.createMergedInvoice();
    } else {
      this.processedInvoices = this.invoices;
    }
  }

  // Get processed invoices based on view mode (cached)
  getProcessedInvoices(): Invoice[] {
    return this.processedInvoices;
  }

  // Create a merged invoice from multiple invoices
  private createMergedInvoice(): Invoice[] {
    if (this.invoices.length === 0) return [];

    const firstInvoice = this.invoices[0];
    const allVehicles: any[] = [];
    let totalAmount = 0;
    const invoiceNumbers: string[] = [];

    // Collect all vehicles and calculate total amount
    this.invoices.forEach(invoice => {
      allVehicles.push({
        ...invoice.vehicle,
        invoice_amount: invoice.invoice_amount,
        invoice_number: invoice.invoice_number,
        item: invoice.item
      });
      totalAmount += invoice.invoice_amount || 0;
      invoiceNumbers.push(invoice.invoice_number);
    });

    // Create merged invoice using first invoice as template
    const mergedInvoice: Invoice = {
      ...firstInvoice,
      invoice_number: invoiceNumbers.join(', '),
      invoice_amount: totalAmount,
      vehicles: allVehicles, // Add all vehicles as a new property
      merged: true // Flag to identify merged invoice
    };

    return [mergedInvoice];
  }

  onEditableDataChange(editableData: any): void {
    this.editableData = editableData;
    console.log('Editable data updated:', this.editableData);
  }

  generatePDF(): void {
    if (this.invoices.length === 0 || this.selectedFormatsInModal.length === 0) {
      return;
    }

    this.isGenerating = true;
    this.currentGeneratingIndex = 0;
    // Total = number of formats (one API call per format for all invoices)
    this.totalToGenerate = this.selectedFormatsInModal.length;

    console.log(`Starting PDF generation for ${this.invoices.length} invoice(s) × ${this.selectedFormatsInModal.length} format(s)`);

    // Generate PDF for each format with all invoices
    this.generateNextPDF(0);
  }

  generateActivePDF(): void {
    if (this.invoices.length === 0 || !this.activeFormatTab) {
      return;
    }

    // Special handling for export certificate format - generate separately for each invoice
    if (this.activeFormatTab === 'export_certificate') {
      this.generateExportCertificatesForActiveTab();
      return;
    }

    this.isGenerating = true;
    this.currentGeneratingIndex = 0;
    this.totalToGenerate = 1; // Only one API call for all invoices with single format

    console.log(`Starting PDF generation for ${this.invoices.length} invoice(s) with active format: ${this.activeFormatTab}`);

    // Prepare data for API call - send all invoice IDs as comma-separated string
    const invoiceIds = this.invoices.map(inv => inv._id).join(',');
    const invoiceNumbers = this.invoices.map(inv => inv.invoice_number).join(', ');

    const data: any = {
      invoice_id: invoiceIds,
      type: this.activeFormatTab
    };

    // Add extra_data if we have editable data (for standard_invoice format)
    if (this.activeFormatTab === 'standard_invoice' && this.editableData) {
      data.extra_data = this.editableData;
    }

    // Check if the format should generate Excel
    const isExcelFormat = this.activeFormatTab === 'shipping_information' || this.activeFormatTab === 'ctn_invoice_excel';

    console.log(`Generating ${isExcelFormat ? 'Excel' : 'PDF'} for invoices: ${invoiceNumbers} (${this.activeFormatTab})`);

    // Check if the export type is Excel format
    if (isExcelFormat) {
      // Use generateTemplateExcel for shipping information
      this.subscriptions.push(
        this._generalService.generateTemplateExcel(data).subscribe({
          next: (res: any) => {
            console.log(`Excel generation response for invoices ${invoiceNumbers}:`, res);
            if (res && res.data && res.data.url) {
              // Download the Excel file
              this.downloadFile(res.data.url, invoiceNumbers, this.activeFormatTab, 'xlsx');

              // Excel generated successfully
              this.isGenerating = false;
              this.currentGeneratingIndex = 0;
              this.totalToGenerate = 0;
              console.log('Excel for active format generated and downloaded successfully');
              alert(`Successfully generated and downloaded Excel for ${this.invoices.length} invoice(s) in ${this.getFormatLabel(this.activeFormatTab)} format`);
            } else {
              console.error('Invalid response format:', res);
              this.isGenerating = false;
              alert(`Excel generation failed for invoices ${invoiceNumbers}. No URL received.`);
            }
          },
          error: (error: any) => {
            console.error(`Error generating Excel for invoices ${invoiceNumbers}:`, error);
            this.isGenerating = false;
            alert(`Error generating Excel for invoices ${invoiceNumbers}. Please try again.`);
          }
        })
      );
    } else {
      // Use generatePDF for other formats
      this.subscriptions.push(
        this._generalService.generatePDF(data).subscribe({
          next: (res: any) => {
            console.log(`PDF generation response for invoices ${invoiceNumbers}:`, res);
            if (res && res.data && res.data.url) {
              // Download the PDF
              this.downloadPDF(res.data.url, invoiceNumbers, this.activeFormatTab);

              // PDF generated successfully
              this.isGenerating = false;
              this.currentGeneratingIndex = 0;
              this.totalToGenerate = 0;
              console.log('PDF for active format generated and downloaded successfully');
              alert(`Successfully generated and downloaded PDF for ${this.invoices.length} invoice(s) in ${this.getFormatLabel(this.activeFormatTab)} format`);
            } else {
              console.error('Invalid response format:', res);
              this.isGenerating = false;
              alert(`PDF generation failed for invoices ${invoiceNumbers}. No URL received.`);
            }
          },
          error: (error: any) => {
            console.error(`Error generating PDF for invoices ${invoiceNumbers}:`, error);
            this.isGenerating = false;
            alert(`Error generating PDF for invoices ${invoiceNumbers}. Please try again.`);
          }
        })
      );
    }
  }

  /**
   * Generate export certificates for active tab
   * Export certificates are generated separately for each invoice/vehicle
   */
  private generateExportCertificatesForActiveTab(): void {
    this.isGenerating = true;
    this.currentGeneratingIndex = 0;
    this.totalToGenerate = this.invoices.length; // One PDF per invoice

    console.log(`Starting export certificate generation for ${this.invoices.length} invoice(s)`);

    // Start generating from first invoice
    this.generateNextExportCertificate(0);
  }

  /**
   * Generate export certificate for a specific invoice
   */
  private generateNextExportCertificate(invoiceIndex: number): void {
    // Check if we've processed all invoices
    if (invoiceIndex >= this.invoices.length) {
      // All export certificates generated
      this.isGenerating = false;
      this.currentGeneratingIndex = 0;
      this.totalToGenerate = 0;
      alert(`Successfully generated and downloaded ${this.invoices.length} export certificate(s)`);
      return;
    }

    // Update progress
    this.currentGeneratingIndex = invoiceIndex + 1;

    const invoice = this.invoices[invoiceIndex];
    const invoiceNumber = invoice.invoice_number;
    const vehicleId = invoice.vehicle?._id;

    if (!vehicleId) {
      console.error(`No vehicle ID found for invoice ${invoiceNumber}`);
      alert(`Error: No vehicle found for invoice ${invoiceNumber}`);
      this.isGenerating = false;
      return;
    }

    // Prepare data for export certificate API call
    const data: any = {
      type: 'vehicle-certificate-invoice',
      vehicle_id: vehicleId
    };

    console.log(`Generating export certificate ${this.currentGeneratingIndex}/${this.totalToGenerate} - Invoice: ${invoiceNumber}, Vehicle ID: ${vehicleId}`);

    // Generate PDF for this export certificate
    this.subscriptions.push(
      this._generalService.generatePDF(data).subscribe({
        next: (res: any) => {
          console.log(`Export certificate generation response for invoice ${invoiceNumber}:`, res);
          if (res && res.data && res.data.url) {
            // Download the PDF
            this.downloadPDF(res.data.url, invoiceNumber, 'export_certificate');

            // Generate next export certificate after a short delay
            setTimeout(() => {
              this.generateNextExportCertificate(invoiceIndex + 1);
            }, 500);
          } else {
            console.error('Invalid response format:', res);
            this.isGenerating = false;
            alert(`Export certificate generation failed for invoice ${invoiceNumber}. No URL received.`);
          }
        },
        error: (error: any) => {
          console.error(`Error generating export certificate for invoice ${invoiceNumber}:`, error);
          this.isGenerating = false;
          alert(`Error generating export certificate for invoice ${invoiceNumber}. Please try again.`);
        }
      })
    );
  }

  /**
   * Generate export certificates in batch mode (when generating all formats)
   * After all certificates are generated, continue with next format
   */
  private generateExportCertificatesInBatch(currentFormatIndex: number): void {
    console.log(`Generating export certificates in batch for ${this.invoices.length} invoice(s)`);
    this.generateExportCertificateInBatch(0, currentFormatIndex);
  }

  /**
   * Generate export certificate for a specific invoice in batch mode
   */
  private generateExportCertificateInBatch(invoiceIndex: number, formatIndex: number): void {
    // Check if we've processed all invoices for this format
    if (invoiceIndex >= this.invoices.length) {
      // All export certificates for this format generated, move to next format
      console.log(`Completed export certificates for all invoices, moving to next format`);
      setTimeout(() => {
        this.generateNextPDF(formatIndex + 1);
      }, 500);
      return;
    }

    const invoice = this.invoices[invoiceIndex];
    const invoiceNumber = invoice.invoice_number;
    const vehicleId = invoice.vehicle?._id;

    if (!vehicleId) {
      console.error(`No vehicle ID found for invoice ${invoiceNumber}, skipping...`);
      // Skip to next invoice
      setTimeout(() => {
        this.generateExportCertificateInBatch(invoiceIndex + 1, formatIndex);
      }, 100);
      return;
    }

    // Prepare data for export certificate API call
    const data: any = {
      type: 'vehicle-certificate-invoice',
      vehicle_id: vehicleId
    };

    console.log(`Generating export certificate for invoice ${invoiceNumber} (${invoiceIndex + 1}/${this.invoices.length})`);

    // Generate PDF for this export certificate
    this.subscriptions.push(
      this._generalService.generatePDF(data).subscribe({
        next: (res: any) => {
          console.log(`Export certificate generation response for invoice ${invoiceNumber}:`, res);
          if (res && res.data && res.data.url) {
            // Download the PDF
            this.downloadPDF(res.data.url, invoiceNumber, 'export_certificate');

            // Generate next export certificate after a short delay
            setTimeout(() => {
              this.generateExportCertificateInBatch(invoiceIndex + 1, formatIndex);
            }, 500);
          } else {
            console.error('Invalid response format:', res);
            // Continue with next invoice even if this one failed
            setTimeout(() => {
              this.generateExportCertificateInBatch(invoiceIndex + 1, formatIndex);
            }, 500);
          }
        },
        error: (error: any) => {
          console.error(`Error generating export certificate for invoice ${invoiceNumber}:`, error);
          // Continue with next invoice even if this one failed
          setTimeout(() => {
            this.generateExportCertificateInBatch(invoiceIndex + 1, formatIndex);
          }, 500);
        }
      })
    );
  }

  private generateNextPDF(formatIndex: number): void {
    // Check if we've processed all formats
    if (formatIndex >= this.selectedFormatsInModal.length) {
      // All PDFs generated
      this.isGenerating = false;
      this.currentGeneratingIndex = 0;
      this.totalToGenerate = 0;
      alert(`Successfully generated and downloaded ${this.selectedFormatsInModal.length} file(s)`);
      return;
    }

    // Calculate current progress
    this.currentGeneratingIndex = formatIndex + 1;

    const format = this.selectedFormatsInModal[formatIndex];

    // Special handling for export certificate format - generate separately for each invoice
    if (format === 'export_certificate') {
      this.generateExportCertificatesInBatch(formatIndex);
      return;
    }

    // Prepare all invoice IDs as comma-separated string
    const invoiceIds = this.invoices.map(inv => inv._id).join(',');
    const invoiceNumbers = this.invoices.map(inv => inv.invoice_number).join(', ');

    // Prepare data for API call
    const data: any = {
      invoice_id: invoiceIds,
      type: format
    };

    // Add extra_data if we have editable data (for standard_invoice format)
    if (format === 'standard_invoice' && this.editableData) {
      data.extra_data = this.editableData;
    }

    // Check if the format should generate Excel
    const isExcelFormat = format === 'shipping_information' || format === 'ctn_invoice_excel';

    console.log(`Generating ${isExcelFormat ? 'Excel' : 'PDF'} ${this.currentGeneratingIndex}/${this.totalToGenerate} - Invoices: ${invoiceNumbers}, Format: ${format}`);

    // Check if the export type is Excel format
    if (isExcelFormat) {
      // Use generateTemplateExcel for shipping information
      this.subscriptions.push(
        this._generalService.generateTemplateExcel(data).subscribe({
          next: (res: any) => {
            console.log(`Excel generation response for invoices ${invoiceNumbers} (${format}):`, res);
            if (res && res.data && res.data.url) {
              // Download the Excel file
              this.downloadFile(res.data.url, invoiceNumbers, format, 'xlsx');

              // Generate next file after a short delay
              setTimeout(() => {
                this.generateNextPDF(formatIndex + 1);
              }, 500);
            } else {
              console.error('Invalid response format:', res);
              this.isGenerating = false;
              alert(`Excel generation failed for invoices ${invoiceNumbers} (${format}). No URL received.`);
            }
          },
          error: (error: any) => {
            console.error(`Error generating Excel for invoices ${invoiceNumbers} (${format}):`, error);
            this.isGenerating = false;
            alert(`Error generating Excel for invoices ${invoiceNumbers} (${format}). Please try again.`);
          }
        })
      );
    } else {
      // Use generatePDF for other formats
      this.subscriptions.push(
        this._generalService.generatePDF(data).subscribe({
          next: (res: any) => {
            console.log(`PDF generation response for invoices ${invoiceNumbers} (${format}):`, res);
            if (res && res.data && res.data.url) {
              // Download the PDF
              this.downloadPDF(res.data.url, invoiceNumbers, format);

              // Generate next PDF after a short delay
              setTimeout(() => {
                this.generateNextPDF(formatIndex + 1);
              }, 500);
            } else {
              console.error('Invalid response format:', res);
              this.isGenerating = false;
              alert(`PDF generation failed for invoices ${invoiceNumbers} (${format}). No URL received.`);
            }
          },
          error: (error: any) => {
            console.error(`Error generating PDF for invoices ${invoiceNumbers} (${format}):`, error);
            this.isGenerating = false;
            alert(`Error generating PDF for invoices ${invoiceNumbers} (${format}). Please try again.`);
          }
        })
      );
    }
  }

  private downloadPDF(url: string, invoiceNumber: string, format: string): void {
    this.downloadFile(url, invoiceNumber, format, 'pdf');
  }

  private downloadFile(url: string, invoiceNumber: string, format: string, extension: string): void {
    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';

    // Get format label for better filename
    const formatLabel = this.getFormatLabel(format);

    // Create filename with invoice number and format
    const filename = `Invoice_${invoiceNumber}_${formatLabel.replace(/\s+/g, '_')}.${extension}`;
    link.download = filename;

    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    console.log(`Downloaded ${extension.toUpperCase()} for invoice ${invoiceNumber} (${formatLabel})`);
  }

  closeModal(): void {
    const modalElement = document.getElementById('kt_modal_view_invoice');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  getFormatLabel(formatValue: string): string {
    const format = this.pdfFormats.find(f => f.value === formatValue);
    return format ? format.label : formatValue;
  }

  openPdf(): void {
    if (this.pdfUrl) {
      window.open(this.pdfUrl, '_blank');
    }
  }
}
